import { useCallback } from 'react';
import { Card } from '../../ui/card';
import { Skeleton } from '../../ui/skeleton';
import { getFiscalYearQuarterString } from '../../../lib/fiscal-year';
import type { QuarterInfo } from '../../../lib/fiscal-year';
import type { Product, AllQuartersData } from '../../../api/shared/types';

const summaryStatusConfig = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
  reopened: { label: 'Re-opened', className: 'bg-blue-100 text-blue-800' },
  'no-project': { label: 'No Project', className: 'bg-gray-100 text-gray-500' },
  'not-available': { label: 'Not Available', className: 'bg-gray-100 text-gray-500' },
} as const;

type DisplayStatus = 'pending' | 'approved' | 'reopened' | 'no-project' | 'not-available';

function getDisplayStatus(
  quarterData: { status?: string; hasAssessedEpics?: boolean; epicCount?: number } | undefined,
): DisplayStatus {
  if (!quarterData?.status) {
    const hasClosedItems = quarterData?.epicCount != null && quarterData.epicCount > 0;
    return hasClosedItems ? 'pending' : 'no-project';
  }
  switch (quarterData.status) {
    case 'approved':
    case 'archived':
      return 'approved';
    case 'reopened':
    case 'withdrawn':
      return 'reopened';
    case 'draft':
    case 'inprogress':
    case 'pendingapproval':
    case 'rejected':
      return 'pending';
    default: {
      const hasClosedItems = quarterData.epicCount != null && quarterData.epicCount > 0;
      return hasClosedItems ? 'pending' : 'no-project';
    }
  }
}

interface ProjectSummaryCardProps {
  product: Product;
  quartersData: AllQuartersData;
  quarters: QuarterInfo[];
  onClick: () => void;
  statusLoading?: boolean;
}

export function ProjectSummaryCard({
  product,
  quartersData,
  quarters,
  onClick,
  statusLoading,
}: ProjectSummaryCardProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  return (
    <Card
      className="transition-all duration-200 cursor-pointer hover:shadow-lg hover:-translate-y-1"
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="p-4">
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <h3 className="font-semibold truncate">{product.project_name}</h3>
            {product.division && (
              <p className="text-sm text-muted-foreground mt-1 truncate">{product.division}</p>
            )}
            {product.software_company && (
              <p className="text-xs text-muted-foreground truncate">{product.software_company}</p>
            )}
          </div>

          {statusLoading ? (
            <div className="grid grid-cols-2 gap-1 shrink-0 w-fit">
              {quarters.map((q) => (
                <Skeleton key={q.quarter} className="h-7 w-24 rounded" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-1 shrink-0 w-fit">
              {quarters.map((q) => {
                const key = getFiscalYearQuarterString(q.fiscalYear, q.quarter);
                const quarterData = quartersData[key];
                const isUnavailable = !q.isAvailable && !quarterData;
                const displayStatus = isUnavailable ? 'not-available' : getDisplayStatus(quarterData);
                const config = summaryStatusConfig[displayStatus];

                return (
                  <div
                    key={q.quarter}
                    className={`flex items-center gap-1.5 rounded px-2 py-1 whitespace-nowrap ${config.className}`}
                  >
                    <span className="text-xs font-semibold">{q.quarter}:</span>
                    <span className="text-xs">{config.label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
